import React, { useState, useEffect, useContext } from 'react';
import ReactDatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import s from './DatePicker.module.scss';
import { useTranslation } from 'next-i18next';
import ButtonPrimary from '../ButtonPrimary/ButtonPrimary';
import Image from 'next/image';
import ButtonSecondary from '../ButtonSecondary/ButtonSecondary';
import { sv, enGB } from 'date-fns/locale';
import { ACTION_TYPES } from '../../containers/EventListPage/EventListPage';
import { EventListPageContext } from '../../containers/EventListPage/EventListPageContext';
import moment from 'moment';
import { trackEventDate } from '../../utils/dataLayer';
import classNames from 'classnames';
import FocusLock from 'react-focus-lock';

registerLocale('sv', sv);
registerLocale('en', enGB);

const DatePicker = ({ isForecastPage = false, onRangeChange, resetKey }) => {
  const { t, i18n } = useTranslation();
  const context = useContext(EventListPageContext);

  // enGB rather than enUS to keep Monday as the first day of the week
  const dateLocale = i18n.language === 'en' ? enGB : sv;

  const dateFrom = context?.state?.dateFrom || null;
  const dateTo = context?.state?.dateTo || null;

  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState([dateFrom, dateTo]);
  const [startDate, endDate] = dateRange;
  const [pendingClear, setPendingClear] = useState(false);

  useEffect(() => {
    if (!isForecastPage) {
      setDateRange([dateFrom, dateTo]);
    }
  }, [dateFrom, dateTo, isForecastPage]);

  useEffect(() => {
    if (resetKey !== undefined) {
      setDateRange([null, null]);
    }
  }, [resetKey]);

  const toggleDatePicker = () => {
    setIsOpen(!isOpen);
  };

  const clearDates = () => {
    setDateRange([null, null]);
    setPendingClear(true);
  };

  const setDateRangeByType = (type) => {
    const today = new Date();
    let newStartDate, newEndDate;

    switch (type) {
      case 'today':
        newStartDate = today;
        newEndDate = null;
        break;
      case 'tomorrow':
        newStartDate = new Date(today);
        newStartDate.setDate(today.getDate() + 1);
        newEndDate = null;
        break;
      case 'nextWeek':
        newStartDate = new Date(today);
        const dayOfWeek = newStartDate.getDay();
        const diffToNextMonday = dayOfWeek === 0 ? 1 : 7 - dayOfWeek + 1;

        newStartDate.setDate(today.getDate() + diffToNextMonday);
        newEndDate = new Date(newStartDate);
        newEndDate.setDate(newStartDate.getDate() + 6);
        break;
      default:
        return;
    }

    setDateRange([newStartDate, newEndDate]);
  };

  const showEvents = () => {
    if (isForecastPage && onRangeChange) {
      onRangeChange(startDate, endDate || startDate);
      setIsOpen(false);
      return;
    }

    if (pendingClear && !startDate) {
      context?.dispatch({
        type: ACTION_TYPES.CHANGE_DATES,
        startDate: null,
        endDate: null,
      });
      setPendingClear(false);
    } else if (startDate) {
      const finalEndDate = endDate ? endDate : startDate;
      context?.dispatch({
        type: ACTION_TYPES.CHANGE_DATES,
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(finalEndDate).format('YYYY-MM-DD'),
      });
    }

    let filterDate = null;

    const today = moment().format('YYYY-MM-DD');
    const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
    const nextWeekStart = moment()
      .day(1 + 7)
      .format('YYYY-MM-DD');
    const nextWeekEnd = moment(nextWeekStart)
      .add(6, 'days')
      .format('YYYY-MM-DD');

    if (
      startDate &&
      !endDate &&
      moment(startDate).format('YYYY-MM-DD') === today
    ) {
      filterDate = 'today';
    } else if (
      startDate &&
      !endDate &&
      moment(startDate).format('YYYY-MM-DD') === tomorrow
    ) {
      filterDate = 'tomorrow';
    } else if (
      startDate &&
      endDate &&
      moment(startDate).format('YYYY-MM-DD') === nextWeekStart &&
      moment(endDate).format('YYYY-MM-DD') === nextWeekEnd
    ) {
      filterDate = 'next week';
    } else if (
      startDate &&
      endDate &&
      !moment(startDate).isSame(endDate, 'day')
    ) {
      filterDate = `${moment(startDate).format('YYYY-MM-DD')}/${moment(endDate).format('YYYY-MM-DD')}`;
    } else if (startDate) {
      filterDate = moment(startDate).format('YYYY-MM-DD');
    }

    trackEventDate(filterDate);

    setIsOpen(false);
  };

  const formattedDateTitle = () => {
    if (!startDate) return t('DatePicker.when');
    if (!endDate || moment(startDate).isSame(endDate, 'day')) {
      return moment(startDate).format('DD MMM');
    }
    return `${moment(startDate).format('DD MMM')} - ${moment(endDate).format('DD MMM')}`;
  };

  return (
    <div className={s.Root}>
      <ButtonPrimary
        icon="icon-calendar"
        isToggle={true}
        modifiers={['InlineFlex']}
        onClick={toggleDatePicker}>
        {formattedDateTitle()}
      </ButtonPrimary>

      {isOpen && (
        <div className={s.ModalContainer}>
          <FocusLock>
            <div className={s.DatePickerContainer}>
              <i className={s.Bg} aria-hidden={true} />
              <TopControls
                setIsOpen={setIsOpen}
                setDateRangeByType={setDateRangeByType}
                t={t}
              />
              <div
                className={classNames(s.DatePickerWrapper, {
                  [s.ForecastPage]: isForecastPage,
                })}>
                <ReactDatePicker
                  selected={startDate}
                  onChange={(update) => {
                    setDateRange(update);
                  }}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange
                  inline
                  monthsShown={2}
                  locale={dateLocale}
                />
              </div>
              <BottomControls
                clearDates={clearDates}
                showEvents={showEvents}
                t={t}
              />
            </div>
          </FocusLock>
        </div>
      )}
    </div>
  );
};

const TopControls = ({ setIsOpen, setDateRangeByType, t }) => {
  return (
    <div className={s.TopControls}>
      <div className={s.TopControlsLeft}>
        <ButtonSecondary
          size="xsmall"
          onClick={() => setDateRangeByType('today')}>
          {t('DatePicker.whenOptions.today')}
        </ButtonSecondary>
        <ButtonSecondary
          size="xsmall"
          onClick={() => setDateRangeByType('tomorrow')}>
          {t('DatePicker.whenOptions.tomorrow')}
        </ButtonSecondary>
        <ButtonSecondary
          size="xsmall"
          onClick={() => setDateRangeByType('nextWeek')}>
          {t('DatePicker.whenOptions.nextWeek')}
        </ButtonSecondary>
      </div>
      <div className={s.TopControlsRight}>
        <button className={s.Close} onClick={() => setIsOpen(false)}>
          <Image
            width={17}
            height={17}
            src={`/img/icon-cross-white.svg`}
            alt=""
          />
        </button>
      </div>
    </div>
  );
};

const BottomControls = ({ clearDates, showEvents, t }) => {
  return (
    <div className={s.BottomControls}>
      <button className={s.ClearButton} onClick={clearDates}>
        <Image width={24} height={24} src={`/img/icon-trash.svg`} alt="" />
        <span className={s.ClearButtonText}>{t('DatePicker.clearDates')}</span>
      </button>
      <ButtonPrimary modifiers={['PurpleBlue']} onClick={showEvents}>
        {t('DatePicker.showEvents')}
      </ButtonPrimary>
    </div>
  );
};

export default DatePicker;
